/**
 * DeveloperToolsPage Component
 *
 * Main page for developer tool integrations including:
 * - SDKs (Node.js, Python, .NET)
 * - Git Hooks
 * - CI/CD (GitHub Actions, Azure Pipelines, GitLab CI)
 * - IDE Extensions (VS Code)
 */
import React, { useState } from 'react';
import {
  Text,
  makeStyles,
  tokens,
  Divider,
  Card,
  Button,
  Badge,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  CodeRegular,
  BranchForkRegular,
  RocketRegular,
  WindowDevToolsRegular,
  BoxRegular,
  KeyRegular,
  ArrowRightRegular,
  PlugConnectedRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiKeysApi } from '../../../services/api';
import { integrationService } from '../../integrations/services';
import { SetupWizardCard } from './SetupWizardCard';
import { GitHooksSetupWizard } from './GitHooksSetupWizard';
import { GitHubActionSetupWizard } from './GitHubActionSetupWizard';
import { AzurePipelinesSetupWizard } from './AzurePipelinesSetupWizard';
import { GitLabCISetupWizard } from './GitLabCISetupWizard';
import { VSCodeExtensionSetupWizard } from './VSCodeExtensionSetupWizard';
import { SDKSetupWizard } from './SDKSetupWizard';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  iconGitHub: {
    fontSize: '24px',
  },
  iconAzure: {
    fontSize: '24px',
    color: '#0078D4',
  },
  iconGitLab: {
    fontSize: '24px',
    color: '#FC6D26',
  },
  iconVSCode: {
    fontSize: '24px',
    color: '#007ACC',
  },
  apiKeyBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalM,
  },
  apiKeyInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

type WizardType =
  | 'git-hooks'
  | 'github-action'
  | 'azure-pipelines'
  | 'gitlab-ci'
  | 'vscode'
  | 'sdk'
  | null;

export const DeveloperToolsPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [activeWizard, setActiveWizard] = useState<WizardType>(null);

  // Fetch active API keys to display the key prefix for setup wizards
  const { data: apiKeys } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => apiKeysApi.getAll(false),
  });

  // Check for repo connections to show cross-link banner
  const { data: githubConns } = useQuery({
    queryKey: ['integrations', 'github', 'connections'],
    queryFn: () => integrationService.getGitHubConnections(),
  });
  const { data: gitlabConns } = useQuery({
    queryKey: ['integrations', 'gitlab', 'connections'],
    queryFn: () => integrationService.getGitLabConnections(),
  });
  const { data: azureConns } = useQuery({
    queryKey: ['integrations', 'azure-devops', 'connections'],
    queryFn: () => integrationService.getAzureDevOpsConnections(),
  });
  const hasRepoConnections = (githubConns?.length ?? 0) + (gitlabConns?.length ?? 0) + (azureConns?.length ?? 0) > 0;

  const activeKey = apiKeys?.find((k) => !k.isRevoked);
  const apiKey = activeKey ? `${activeKey.keyPrefix}...` : '';
  const hasApiKey = !!activeKey;

  const closeWizard = () => setActiveWizard(null);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text size={600} weight="semibold">
          Developer Tools
        </Text>
        <Text size={300}>
          Integrate LeftShield into your development workflow with SDKs, git hooks, CI/CD pipelines, and IDE extensions.
        </Text>
      </div>

      {/* API Key Status */}
      {!hasApiKey && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>No API key generated</MessageBarTitle>
            An API key is required to authenticate developer tool integrations.{' '}
            <Button
              appearance="transparent"
              size="small"
              icon={<ArrowRightRegular />}
              iconPosition="after"
              as="a"
              href="/settings/api-keys"
            >
              Generate API Key
            </Button>
          </MessageBarBody>
        </MessageBar>
      )}
      {hasApiKey && (
        <Card className={styles.apiKeyBanner}>
          <div className={styles.apiKeyInfo}>
            <KeyRegular />
            <Text size={300}>
              Using API key: <Badge appearance="outline" size="medium">{activeKey?.name}</Badge> ({activeKey?.keyPrefix}...)
            </Text>
          </div>
          <Button
            appearance="subtle"
            size="small"
            as="a"
            href="/settings/api-keys"
          >
            Manage Keys
          </Button>
        </Card>
      )}

      {/* Cross-link: Connect repos first */}
      {!hasRepoConnections && (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>Connect your repositories first</MessageBarTitle>
            Set up a GitHub, GitLab, or Azure DevOps connection to get the most out of your developer tools.{' '}
            <Button
              appearance="transparent"
              size="small"
              icon={<PlugConnectedRegular />}
              iconPosition="after"
              onClick={() => navigate('/integrations')}
            >
              Go to Integrations
            </Button>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* SDKs Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <BoxRegular />
          <Text size={500} weight="semibold">SDKs</Text>
        </div>
        <div className={styles.cardGrid}>
          <SetupWizardCard
            title="LeftShield SDK"
            description="Programmatic access to code validation for Node.js, Python, and .NET applications"
            icon={<CodeRegular fontSize={24} />}
            badge="npm / PyPI / NuGet"
            onClick={() => setActiveWizard('sdk')}
          />
        </div>
      </div>

      <Divider />

      {/* Git Hooks Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <BranchForkRegular />
          <Text size={500} weight="semibold">Git Hooks</Text>
        </div>
        <Text size={200}>
          Validate code before commits and pushes to catch issues early in development.
        </Text>
        <div className={styles.cardGrid}>
          <SetupWizardCard
            title="Git Hooks Package"
            description="Pre-commit and pre-push hooks for local development validation"
            icon={<BranchForkRegular fontSize={24} />}
            badge="npm"
            onClick={() => setActiveWizard('git-hooks')}
          />
        </div>
      </div>

      <Divider />

      {/* CI/CD Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <RocketRegular />
          <Text size={500} weight="semibold">CI/CD Pipelines</Text>
        </div>
        <Text size={200}>
          Automate security scanning in your continuous integration pipelines.
        </Text>
        <div className={styles.cardGrid}>
          <SetupWizardCard
            title="GitHub Actions"
            description="Validate code on pull requests and pushes with SARIF support"
            icon={
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            }
            badge="GitHub Marketplace"
            onClick={() => setActiveWizard('github-action')}
          />
          <SetupWizardCard
            title="Azure Pipelines"
            description="Build task for Azure DevOps pipelines with build summary integration"
            icon={
              <svg viewBox="0 0 24 24" width="24" height="24" fill="#0078D4">
                <path d="M0 8.877L2.247 5.91l8.405-3.416V.022l7.37 5.393L2.966 8.338v8.225L0 15.707zm24-4.45v14.651l-5.753 4.9-9.303-3.057v3.056l-5.978-7.416 15.057 1.798V5.415z"/>
              </svg>
            }
            badge="VS Marketplace"
            onClick={() => setActiveWizard('azure-pipelines')}
          />
          <SetupWizardCard
            title="GitLab CI"
            description="Include template for GitLab CI/CD pipelines with MR integration"
            icon={
              <svg viewBox="0 0 24 24" width="24" height="24" fill="#FC6D26">
                <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024"/>
              </svg>
            }
            badge="Template"
            onClick={() => setActiveWizard('gitlab-ci')}
          />
        </div>
      </div>

      <Divider />

      {/* IDE Extensions Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <WindowDevToolsRegular />
          <Text size={500} weight="semibold">IDE Extensions</Text>
        </div>
        <Text size={200}>
          Get real-time validation feedback while you code.
        </Text>
        <div className={styles.cardGrid}>
          <SetupWizardCard
            title="VS Code Extension"
            description="Real-time validation with inline diagnostics and quick fixes"
            icon={
              <svg viewBox="0 0 24 24" width="24" height="24" fill="#007ACC">
                <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
              </svg>
            }
            badge="VS Code Marketplace"
            onClick={() => setActiveWizard('vscode')}
          />
        </div>
      </div>

      {/* Wizards */}
      <GitHooksSetupWizard
        open={activeWizard === 'git-hooks'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
      <GitHubActionSetupWizard
        open={activeWizard === 'github-action'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
      <AzurePipelinesSetupWizard
        open={activeWizard === 'azure-pipelines'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
      <GitLabCISetupWizard
        open={activeWizard === 'gitlab-ci'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
      <VSCodeExtensionSetupWizard
        open={activeWizard === 'vscode'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
      <SDKSetupWizard
        open={activeWizard === 'sdk'}
        onClose={closeWizard}
        apiKey={apiKey}
      />
    </div>
  );
};

export default DeveloperToolsPage;
